import { Controller } from "@hotwired/stimulus"

/**
 * Detects the browser/device timezone and writes it into a `tz` cookie so
 * Rails can use it for server-rendered calendar pages (CalendarsController
 * and BookingsController both read `client_time_zone` / `calendar_time_zone`
 * from that cookie).
 *
 * It ONLY sets the cookie — it deliberately does NOT reload. The cookie takes
 * effect on the next request (any calendar navigation, or the next visit),
 * which keeps it safe for Turbo and system tests (no DOM detach mid-`visit`)
 * and avoids a reload flash. The same JS runs inside the mobile WebView, so no
 * native change is needed.
 *
 * Trade-off: a fresh login landing directly on /calendar paints once in the
 * server fallback zone (UTC). That's intentional — CalendarsController pads the
 * fetch window by ±1 day so no edge bookings are dropped (and ignores any
 * legacy `?timezone=` param — the cookie is the only zone signal), and the next
 * navigation re-renders in the device zone. Do NOT restore a reload here: it
 * fired right after Capybara's `visit` and caused StaleReferenceErrors.
 *
 * Mounted on <body> of the hotwire and mobile layouts (CONEK-4245 phase 1).
 */
export default class extends Controller {
  connect() {
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (!browserTz) return
    // Already correct — avoid rewriting the cookie on every connect.
    if (this._readTzCookie() === browserTz) return

    document.cookie = `tz=${browserTz}; path=/; max-age=31536000; samesite=lax`
  }

  _readTzCookie() {
    const match = document.cookie
      .split("; ")
      .find((row) => row.startsWith("tz="))
    return match ? match.split("=")[1] : null
  }
}
